import {
  Component,
  ViewChild,
  OnDestroy,
  AfterViewInit,
  OnInit,
  Inject,
  PLATFORM_ID,
} from '@angular/core';
import { isPlatformBrowser, DecimalPipe, DOCUMENT } from '@angular/common';
import { Meta, Title } from '@angular/platform-browser';
import { MapComponent } from '../../map/map.component';
import { Mappa } from '../../types/payload-types';
import { PayloadService } from '../../services/payload.service';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import {
  Subject,
  Subscription,
  debounceTime,
  distinctUntilChanged,
  switchMap,
  of,
  Observable,
  catchError,
} from 'rxjs';

@Component({
  selector: 'app-mappa',
  standalone: true,
  imports: [MapComponent, FormsModule, DecimalPipe],
  templateUrl: './mappa.component.html',
  styleUrl: './mappa.component.scss',
})
export class MappaComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(MapComponent) mapComponent!: MapComponent;
  data: Mappa | undefined;
  searchValue: string = '';
  isLoading: boolean = false;
  error: string = '';

  private searchSubject = new Subject<string>();
  private subscriptions: Subscription[] = [];
  private isBrowser: boolean;
  private document: Document;
  private meta: Meta;
  private title: Title;

  currentCoordinates: { lat: number; lng: number } | null = null;
  private dataLoaded = false;
  sortedChiese: Array<{
    nome: string;
    coordinate: { lat: number; lng: number };
    indirizzo?: string | null;
    orari?: string | null;
    telefono?: string | null;
    id?: string | null;
    distance?: number;
  }> = [];

  constructor(
    private payloadService: PayloadService,
    private http: HttpClient,
    @Inject(PLATFORM_ID) platformId: Object,
    @Inject(DOCUMENT) document: Document,
    meta: Meta,
    title: Title,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    this.document = document;
    this.meta = meta;
    this.title = title;
  }
  ngOnInit(): void {
    this.setSeoMeta();

    const dataSub = this.payloadService.getMappa().subscribe((res: Mappa) => {
      this.data = res;
      this.dataLoaded = true;
      this.setJsonLd();
      this.autoUsePositionIfReady();
    });
    this.subscriptions.push(dataSub);

    // Setup search debouncing
    const searchSub = this.searchSubject
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        switchMap((searchTerm) => this.geocodeCity(searchTerm)),
      )
      .subscribe((result) => {
        if (result) {
          this.error = ''; // Clear any previous error
          this.currentCoordinates = { lat: result.lat, lng: result.lng };
          this.mapComponent?.focusOnCoordinates(result.lat, result.lng, 12);
          this.sortChurchesByDistance();
        }
      });
    this.subscriptions.push(searchSub);
  }

  ngAfterViewInit(): void {
    this.autoUsePositionIfReady();
  }

  ngOnDestroy(): void {
    this.searchSubject.complete();
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  private autoUsePositionIfReady(): void {
    // Only auto-trigger if browser, data is loaded and view is initialized
    if (this.isBrowser && this.dataLoaded && this.mapComponent) {
      this.usePosition();
    }
  }

  private geocodeCity(
    cityName: string,
  ): Observable<{ lat: number; lng: number } | null> {
    if (!cityName || cityName.trim().length < 2) {
      return of(null);
    }

    const url = `/nominatim/search?format=json&q=${encodeURIComponent(
      cityName,
    )}&limit=1&addressdetails=1`;

    return this.http.get<any[]>(url).pipe(
      switchMap((results) => {
        if (results && results.length > 0) {
          const result = results[0];
          return of({
            lat: parseFloat(result.lat),
            lng: parseFloat(result.lon),
          });
        }
        return of(null);
      }),
      catchError((err) => {
        console.error('Geocoding error:', err);
        this.error = 'Impossibile trovare la città';
        return of(null);
      }),
    );
  }

  private getCityName(lat: number, lng: number) {
    // Using OpenStreetMap Nominatim API for reverse geocoding (free)
    const url = `/nominatim/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`;

    this.http
      .get<any>(url)
      .pipe(
        catchError((err) => {
          this.isLoading = false;
          this.error = 'Failed to get location details';
          console.error('Geocoding error:', err);
          return of(null);
        }),
      )
      .subscribe((response) => {
        this.isLoading = false;
        if (response && response.address) {
          this.error = ''; // Clear any previous error
          // Try to get city name in order of preference
          const city =
            response.address.city ||
            response.address.town ||
            response.address.village ||
            response.address.municipality ||
            response.address.county ||
            'Unknown location';

          this.searchValue = city;
        } else {
          this.error = 'Could not determine city name';
        }
      });
  }

  usePosition() {
    if (!this.isBrowser) return;

    this.isLoading = true;
    this.error = '';

    if (!navigator.geolocation) {
      this.error = 'Geolocation is not supported by this browser';
      this.isLoading = false;
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;

        // Update current coordinates
        this.currentCoordinates = { lat, lng };

        // Focus map on user location and show user marker
        this.mapComponent?.focusOnCoordinates(lat, lng, 12);
        this.mapComponent?.setUserLocation(lat, lng);

        // Sort churches by distance
        this.sortChurchesByDistance();

        this.getCityName(lat, lng);
      },
      (error) => {
        this.isLoading = false;
        switch (error.code) {
          case error.PERMISSION_DENIED:
            this.error = 'Location access denied by user';
            break;
          case error.POSITION_UNAVAILABLE:
            this.error = 'Location information is unavailable';
            break;
          case error.TIMEOUT:
            this.error = 'Location request timed out';
            break;
          default:
            this.error = 'An unknown error occurred';
            break;
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000, // 5 minutes
      },
    );
  }

  // Method to focus on specific church
  focusOnChurch(churchId: string) {
    this.mapComponent?.focusOnChurch(churchId);

    // Scroll to top of page only on mobile (< 768px)
    if (this.isBrowser && window.innerWidth < 768) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  // Method to focus on coordinates
  focusOnCoordinates(lat: number, lng: number, zoom: number = 12) {
    this.mapComponent?.focusOnCoordinates(lat, lng, zoom);
  }

  // Method called when search input changes
  onSearchChange() {
    this.searchSubject.next(this.searchValue);
  }

  // Calculate distance between two coordinates using Haversine formula
  private calculateDistance(
    lat1: number,
    lng1: number,
    lat2: number,
    lng2: number,
  ): number {
    const R = 6371; // Earth's radius in kilometers
    const dLat = this.toRadians(lat2 - lat1);
    const dLng = this.toRadians(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRadians(lat1)) *
        Math.cos(this.toRadians(lat2)) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Distance in kilometers
  }

  private toRadians(degrees: number): number {
    return degrees * (Math.PI / 180);
  }

  // Sort churches by distance from current location
  private sortChurchesByDistance() {
    if (!this.currentCoordinates || !this.data?.chiese) return;

    this.sortedChiese = this.data.chiese
      .filter(
        (chiesa) =>
          chiesa.coordinate?.lat != null && chiesa.coordinate?.lng != null,
      )
      .map((chiesa) => ({
        ...chiesa,
        distance: this.calculateDistance(
          this.currentCoordinates!.lat,
          this.currentCoordinates!.lng,
          chiesa.coordinate.lat,
          chiesa.coordinate.lng,
        ),
      }))
      .sort((a, b) => (a.distance || 0) - (b.distance || 0));
  }

  private setSeoMeta(): void {
    const pageTitle = 'Trova una Chiesa | ComeTralci';
    const description =
      'Trova la chiesa evangelica ComeTralci più vicina a te. Consulta indirizzi, orari e numeri di telefono delle nostre chiese in Italia.';
    const canonicalUrl = 'https://cometralci.com/mappa';

    this.title.setTitle(pageTitle);
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({ property: 'og:title', content: pageTitle });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:url', content: canonicalUrl });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ name: 'twitter:card', content: 'summary' });
    this.meta.updateTag({ name: 'twitter:title', content: pageTitle });
    this.meta.updateTag({ name: 'twitter:description', content: description });
    this.updateCanonicalUrl(canonicalUrl);
  }

  private setJsonLd(): void {
    if (!this.data?.chiese?.length) return;

    const churches = this.data.chiese.map((chiesa) => ({
      '@type': 'PlaceOfWorship',
      name: chiesa.nome,
      ...(chiesa.indirizzo && {
        address: {
          '@type': 'PostalAddress',
          streetAddress: chiesa.indirizzo,
        },
      }),
      ...(chiesa.telefono && { telephone: chiesa.telefono }),
      ...(chiesa.orari && { openingHours: chiesa.orari }),
      ...(chiesa.coordinate?.lat != null && {
        geo: {
          '@type': 'GeoCoordinates',
          latitude: chiesa.coordinate.lat,
          longitude: chiesa.coordinate.lng,
        },
      }),
    }));

    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'ComeTralci',
      url: 'https://cometralci.com',
      location: churches,
    };

    const existing = this.document.getElementById('mappa-jsonld');
    if (existing) existing.remove();

    const script = this.document.createElement('script');
    script.id = 'mappa-jsonld';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(jsonLd);
    this.document.head.appendChild(script);
  }

  private updateCanonicalUrl(url: string): void {
    let link = this.document.querySelector(
      'link[rel="canonical"]',
    ) as HTMLLinkElement | null;
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }
    link.setAttribute('href', url);
  }
}
